// src/context/ThemeProvider.tsx
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme, CssBaseline, Theme, ThemeOptions } from '@mui/material';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import PaletteIcon from '@mui/icons-material/Palette';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import NightsStayIcon from '@mui/icons-material/NightsStay';
import WbTwilightIcon from '@mui/icons-material/WbTwilight';
import CandlestickChartIcon from '@mui/icons-material/CandlestickChart';
import '@fontsource/inter/400.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';

export type ThemeMode = 'light' | 'vivid' | 'sand' | 'dark' | 'midnight' | 'oled' | 'emerald';

export interface ThemeMeta {
  id: ThemeMode;
  label: string;
  /** 2-3 hex preview colors: [primary, secondary, background] */
  swatch: string[];
  isDark: boolean;
}

export const THEMES: ThemeMeta[] = [
  { id: 'light',    label: 'Light',    swatch: ['#3B82F6', '#6366F1', '#F1F5F9'], isDark: false },
  { id: 'vivid',    label: 'Vivid',    swatch: ['#FF6B6B', '#4ECDC4', '#F8FAFC'], isDark: false },
  { id: 'sand',     label: 'Sand',     swatch: ['#B45309', '#0F766E', '#F6F1E7'], isDark: false },
  { id: 'dark',     label: 'Dark',     swatch: ['#2DD4BF', '#818CF8', '#0F172A'], isDark: true },
  { id: 'midnight', label: 'Midnight', swatch: ['#A78BFA', '#22D3EE', '#131033'], isDark: true },
  { id: 'oled',     label: 'OLED',     swatch: ['#00E5FF', '#0A0A0A', '#000000'], isDark: true },
  { id: 'emerald',  label: 'Emerald',  swatch: ['#2FD3A5', '#E5B567', '#14161B'], isDark: true },
];

const THEME_IDS: ThemeMode[] = THEMES.map((t) => t.id);

const isThemeMode = (value: string | null): value is ThemeMode =>
  value !== null && (THEME_IDS as string[]).includes(value);

interface ThemeContextType {
  themeMode: ThemeMode;
  toggleThemeMode: () => void;
  setThemeMode: (mode: ThemeMode) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useThemeContext must be used within a ThemeProvider');
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

const baseOptions: ThemeOptions = {
  typography: {
    fontFamily: 'Inter, Arial, sans-serif',
    button: {
      fontWeight: 600,
      textTransform: 'none' as const,
      letterSpacing: '0.5px',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiPaper: {
      styleOverrides: {
        root: {
          backdropFilter: 'blur(10px)',
          borderRadius: '12px',
          border: '1px solid',
          transition: 'all 0.3s ease',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          fontWeight: 600,
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-1px)',
            boxShadow: (theme: Theme) => theme.shadows[4],
          },
        },
        contained: {
          boxShadow: 'none',
          '&:hover': {
            boxShadow: (theme: Theme) => theme.shadows[4],
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          transition: 'all 0.2s ease-in-out',
        },
      },
    },
  },
};

/** Paper treatment for light themes: soft border, gentle shadow, keeps the frosted blur. */
const lightPaper = (borderColor: string, boxShadow: string) => ({
  MuiPaper: {
    styleOverrides: {
      root: {
        backdropFilter: 'blur(10px)',
        borderRadius: '12px',
        border: `1px solid ${borderColor}`,
        boxShadow,
        transition: 'all 0.3s ease',
      },
    },
  },
});

/** Paper treatment for dark themes: subtle light-alpha border, no gradient overlay, no blur artifacts. */
const darkPaper = (borderColor: string, boxShadow: string) => ({
  MuiPaper: {
    styleOverrides: {
      root: {
        backdropFilter: 'none',
        backgroundImage: 'none',
        borderRadius: '12px',
        border: `1px solid ${borderColor}`,
        boxShadow,
        transition: 'all 0.3s ease',
      },
    },
  },
});

const getTheme = (mode: ThemeMode): Theme => {
  switch (mode) {
    // ── Vivid: playful coral/teal on cool white ─────────────────────────
    case 'vivid':
      return createTheme({
        ...baseOptions,
        palette: {
          mode: 'light',
          primary: { main: '#FF6B6B', contrastText: '#FFFFFF' },
          secondary: { main: '#4ECDC4', contrastText: '#0F172A' },
          background: {
            default: '#F8FAFC',
            paper: 'rgba(255,255,255,0.95)',
          },
          text: { primary: '#0F172A', secondary: '#64748B' },
          divider: 'rgba(0,0,0,0.05)',
        },
        components: {
          ...baseOptions.components,
          ...lightPaper('rgba(0,0,0,0.08)', '0 4px 20px rgba(0,0,0,0.05)'),
        },
      });

    // ── Sand: warm solar light — parchment surfaces, amber + deep teal ──
    case 'sand':
      return createTheme({
        ...baseOptions,
        palette: {
          mode: 'light',
          primary: { main: '#B45309', contrastText: '#FFFFFF' },
          secondary: { main: '#0F766E', contrastText: '#FFFFFF' },
          background: {
            default: '#F1EADB',
            paper: '#FBF7EE',
          },
          text: { primary: '#3F3428', secondary: '#7C7062' },
          divider: 'rgba(120,85,40,0.14)',
          action: { hover: 'rgba(180,83,9,0.06)' },
        },
        components: {
          ...baseOptions.components,
          ...lightPaper('rgba(120,85,40,0.16)', '0 4px 18px rgba(120,85,40,0.08)'),
          MuiChip: {
            styleOverrides: {
              root: {
                transition: 'all 0.2s ease-in-out',
                borderRadius: '8px',
              },
            },
          },
        },
      });

    // ── Dark: refined slate/teal — the workhorse dark mode ──────────────
    case 'dark':
      return createTheme({
        ...baseOptions,
        palette: {
          mode: 'dark',
          primary: { main: '#2DD4BF', contrastText: '#042F2E' },
          secondary: { main: '#818CF8', contrastText: '#0B1120' },
          background: {
            default: '#0B1120',
            paper: '#111A2E',
          },
          text: { primary: '#E2E8F0', secondary: '#94A3B8' },
          divider: 'rgba(148,163,184,0.14)',
          action: { hover: 'rgba(45,212,191,0.08)' },
        },
        components: {
          ...baseOptions.components,
          ...darkPaper('rgba(148,163,184,0.14)', '0 4px 24px rgba(2,6,17,0.45)'),
          MuiTooltip: {
            styleOverrides: {
              tooltip: { backgroundColor: '#1E2A44', color: '#E2E8F0' },
            },
          },
        },
      });

    // ── Midnight: deep indigo/violet with an electric cyan counterpoint ─
    case 'midnight':
      return createTheme({
        ...baseOptions,
        palette: {
          mode: 'dark',
          primary: { main: '#A78BFA', contrastText: '#160B33' },
          secondary: { main: '#22D3EE', contrastText: '#04262C' },
          background: {
            default: '#0B0921',
            paper: '#151238',
          },
          text: { primary: '#EDEAFF', secondary: '#9C95C9' },
          divider: 'rgba(167,139,250,0.16)',
          action: { hover: 'rgba(167,139,250,0.10)' },
        },
        components: {
          ...baseOptions.components,
          ...darkPaper('rgba(167,139,250,0.18)', '0 6px 28px rgba(4,2,20,0.55)'),
          MuiChip: {
            styleOverrides: {
              root: {
                transition: 'all 0.2s ease-in-out',
                borderRadius: '9999px',
              },
            },
          },
          MuiTooltip: {
            styleOverrides: {
              tooltip: { backgroundColor: '#251F52', color: '#EDEAFF' },
            },
          },
        },
      });

    // ── OLED: true black, near-black paper, one vivid cyan accent ───────
    case 'oled':
      return createTheme({
        ...baseOptions,
        palette: {
          mode: 'dark',
          primary: { main: '#00E5FF', contrastText: '#00171B' },
          secondary: { main: '#B0B8C1', contrastText: '#000000' },
          background: {
            default: '#000000',
            paper: '#0A0A0A',
          },
          text: { primary: '#FFFFFF', secondary: '#9AA1A9' },
          divider: 'rgba(255,255,255,0.12)',
          action: { hover: 'rgba(0,229,255,0.08)' },
        },
        components: {
          ...baseOptions.components,
          // On pure black, borders do the lifting — no shadows (they read as smudges).
          ...darkPaper('rgba(255,255,255,0.14)', 'none'),
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: '8px',
                fontWeight: 600,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 0 16px rgba(0,229,255,0.35)',
                },
              },
              contained: {
                boxShadow: 'none',
                '&:hover': { boxShadow: '0 0 16px rgba(0,229,255,0.35)' },
              },
            },
          },
          MuiTooltip: {
            styleOverrides: {
              tooltip: { backgroundColor: '#161616', color: '#FFFFFF', border: '1px solid rgba(255,255,255,0.14)' },
            },
          },
        },
      });

    // ── Emerald: graphite trade-desk with a signal-green accent ─────────
    case 'emerald':
      return createTheme({
        ...baseOptions,
        palette: {
          mode: 'dark',
          primary: { main: '#2FD3A5', contrastText: '#04130E' },
          secondary: { main: '#E5B567', contrastText: '#1C1204' },
          background: {
            default: '#0A0B0E',
            paper: '#14161B',
          },
          text: { primary: '#E6E9EF', secondary: '#8C93A3' },
          divider: 'rgba(230,233,239,0.09)',
          action: { hover: 'rgba(47,211,165,0.08)' },
          success: { main: '#2FD3A5' },
          error: { main: '#F0616D' },
        },
        components: {
          ...baseOptions.components,
          ...darkPaper('rgba(230,233,239,0.09)', '0 4px 22px rgba(0,0,0,0.5)'),
          MuiChip: {
            styleOverrides: {
              root: {
                transition: 'all 0.2s ease-in-out',
                borderRadius: '6px',
                fontWeight: 600,
              },
            },
          },
          MuiTooltip: {
            styleOverrides: {
              tooltip: { backgroundColor: '#1C1F26', color: '#E6E9EF' },
            },
          },
        },
      });

    // ── Light: calm blue/indigo default ─────────────────────────────────
    default:
      return createTheme({
        ...baseOptions,
        palette: {
          mode: 'light',
          primary: { main: '#3B82F6', contrastText: '#FFFFFF' },
          secondary: { main: '#6366F1', contrastText: '#FFFFFF' },
          background: {
            default: '#F1F5F9',
            paper: 'rgba(255,255,255,0.95)',
          },
          text: { primary: '#1E293B', secondary: '#64748B' },
          divider: 'rgba(0,0,0,0.05)',
        },
        components: {
          ...baseOptions.components,
          ...lightPaper('rgba(0,0,0,0.08)', '0 4px 15px rgba(0,0,0,0.03)'),
        },
      });
  }
};

const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => {
    const storedTheme = localStorage.getItem('themeMode');
    return isThemeMode(storedTheme) ? storedTheme : 'light';
  });

  useEffect(() => {
    localStorage.setItem('themeMode', themeMode);
  }, [themeMode]);

  const setThemeMode = (mode: ThemeMode) => {
    setThemeModeState(mode);
  };

  const toggleThemeMode = () => {
    setThemeModeState((prev) => {
      const nextIndex = (THEME_IDS.indexOf(prev) + 1) % THEME_IDS.length;
      return THEME_IDS[nextIndex];
    });
  };

  return (
    <ThemeContext.Provider value={{ themeMode, toggleThemeMode, setThemeMode }}>
      <MuiThemeProvider theme={getTheme(themeMode)}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};

export const getThemeIcon = (themeMode: ThemeMode) => {
  switch (themeMode) {
    case 'light':
      return <Brightness7Icon sx={{ color: 'text.secondary' }} />;
    case 'vivid':
      return <PaletteIcon sx={{ color: 'secondary.main' }} />;
    case 'sand':
      return <WbTwilightIcon sx={{ color: 'primary.main' }} />;
    case 'dark':
      return <Brightness4Icon sx={{ color: 'primary.main' }} />;
    case 'midnight':
      return <NightsStayIcon sx={{ color: 'primary.main' }} />;
    case 'oled':
      return <DarkModeIcon sx={{ color: 'primary.main' }} />;
    case 'emerald':
      return <CandlestickChartIcon sx={{ color: 'primary.main' }} />;
    default:
      return <Brightness7Icon />;
  }
};

export default ThemeProvider;
